import type { InspectionType } from '@/types';

// ============================================================
// Manual-only checks by inspection type
// These are functional checks that cannot be assessed from photos
// ============================================================

const MANUAL_CHECKS_BY_TYPE: Record<InspectionType, string[]> = {
  daily: [
    'lt-hazard',      // Hazard / emergency flashers
    'int-clean',      // Cab clean & organized (optional visual)
  ],
  weekly: [
    // All daily manual checks
    'lt-hazard',
    'int-clean',
    // Weekly additions
    'br-pedal',       // Brake pedal firm
    'br-parking',     // Parking brake
    'br-noise',       // Brake noise
    'st-play',        // Steering play
    'st-power',       // Power steering
    'int-horn',       // Horn
    'int-hvac',       // HVAC
    'int-seat',       // Seat & seatbelt
  ],
  monthly: [
    // All daily manual checks
    'lt-hazard',
    'int-clean',
    // All weekly manual checks
    'br-pedal',
    'br-parking',
    'br-noise',
    'st-play',
    'st-power',
    'int-horn',
    'int-hvac',
    'int-seat',
    // Monthly has no additional manual-only checks
    // (safety equipment and fluids are covered by photos)
  ],
};

// ============================================================
// Samsara-data checks by inspection type
// ============================================================
const SAMSARA_CHECKS_BY_TYPE: Record<InspectionType, string[]> = {
  daily: [],
  weekly: ['tr-pressure'],
  monthly: ['tr-pressure'],
};

// ============================================================
// Image + Manual checks — physical damage via photo,
// functionality confirmed manually
// ============================================================
export const IMAGE_AND_MANUAL_CHECK_IDS = new Set([
  'lt-headlights',
  'lt-tail',
  'lt-brake',
  'lt-turn',
  'lt-reverse',
]);

// Image + manual checks that apply per type
const IMAGE_AND_MANUAL_BY_TYPE: Record<InspectionType, string[]> = {
  daily: ['lt-headlights', 'lt-tail', 'lt-brake', 'lt-turn', 'lt-reverse'],
  weekly: ['lt-headlights', 'lt-tail', 'lt-brake', 'lt-turn', 'lt-reverse'],
  monthly: ['lt-headlights', 'lt-tail', 'lt-brake', 'lt-turn', 'lt-reverse'],
};

// ============================================================
// Exported helpers
// ============================================================

export function getManualCheckIds(type: InspectionType): Set<string> {
  const manual = MANUAL_CHECKS_BY_TYPE[type] ?? [];
  const imageAndManual = IMAGE_AND_MANUAL_BY_TYPE[type] ?? [];
  return new Set([...manual, ...imageAndManual]);
}

export function getSamsaraCheckIds(type: InspectionType): Set<string> {
  return new Set(SAMSARA_CHECKS_BY_TYPE[type] ?? []);
}

// Keep backward compat for any code still using MANUAL_ONLY_CHECK_IDS
// defaults to weekly which has the most checks
export const MANUAL_ONLY_CHECK_IDS = new Set(MANUAL_CHECKS_BY_TYPE.weekly);
export const SAMSARA_CHECK_IDS = new Set(SAMSARA_CHECKS_BY_TYPE.weekly);